/* eslint-disable
	no-useless-escape,
	no-await-in-loop,
*/
import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import MinusIcon from '@mui/icons-material/Remove';
import InfoIcon from '@mui/icons-material/Info';
import DoneIcon from '@mui/icons-material/Done';
import IconButton from '@mui/material/IconButton';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import Constants from 'relevant-shared/reportData/constants';
import AllMappingDimensions from 'relevant-shared/mappingDimensions/allMappingDimension';
import { onceEvent } from 'relevant-shared/misc/misc';
import DeleteButton from '../DeleteButton';
import TextField from '../TextField';
import { ActionButton } from '../ActionButton/ActionButton';
import DataTable from '../DataTable';
import DateUtils from '../../lib/dateUtils';
import OperationWrapper from '../OperationWrapper';
import { Reports } from '../../api/relevant';
import SystemData from '../../lib/systemData';
import JobButton from '../JobButton';
import Base from '../../layouts/Base';
import classes from '../../api/classes';
import { ConfirmDialog } from '../ConfirmDialog';
import { Dialog } from '../Dialog';
import { Tooltip } from '../Tooltip';
import MiscUtils from '../../lib/miscUtils';

const CANDIDATE_CHUNK = 5;
const MAX_CHECK_FIRST_WORD_CHARS = 4;
const MAX_ONLY_CHECK_FIRST_WORD_CHARS = 2;
const MAX_ADVERTISERS = 20000;
const HISTORY_DAYS_BACK = 60;
const MAX_ADV_SETUP_CHUNK_MS = 20;
const SPLIT_REGEXP = /[\s\,\.\-\[\]\(\)\_]/;
const FORBIDDEN_WORDS = Object.getOwnPropertyNames(Object.getPrototypeOf({}));
const FORBIDDEN_WORD_MAP = _.zipObject(FORBIDDEN_WORDS, Array(FORBIDDEN_WORDS.length).fill(true));

const ByMappingProp = _.keyBy(AllMappingDimensions, 'reportProp');

const splitStr = (str) => str
	.toLowerCase()
	.split(SPLIT_REGEXP)
	.filter((w) => w.length && !FORBIDDEN_WORD_MAP[w]);

const loadingMarker = () => (
	<>
		<CircularProgress size="1.5em" color="inherit" sx={{ mr: 2 }} />
		<b style={{ color: 'gray' }}>Loading...</b>
	</>
);

// TODO: This component should be redesigned and renamed. Currently works with
//  "Advertiser" and "Buyer", but variable names still follow the "Advertiser"
//  naming convention
class DynamicSearchFilter extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			searchStr: '',
			candidates: [],
			chunkSz: CANDIDATE_CHUNK,
			dimensionData: {
				byNr: {},
				byFirstWord: {},
				byAnyWord: {},
				byAnyWordFull: {},
			},
		};
		this.hadInputEvent = onceEvent();
		this.searchContainerElm = null;
		this.initAdvDataPromise = new Promise((initResolve, initReject) => {
			Object.assign(this, { initResolve, initReject });
		});
	}

	componentDidMount() {
		this.globalListener = (event) => {
			if (!this.state.showSearchResult) {
				return;
			}
			if (!this.searchContainerElm) {
				return;
			}
			if (event.type === 'keyup') {
				if (event.keyCode !== 27) {
					return;
				}
			} else {
				let element = event.target;
				while (element) {
					if (element === this.searchContainerElm) return;
					element = element.parentNode;
				}
			}
			event.stopPropagation();
			event.preventDefault();
			this.updateState({ showSearchResult: false });
		};
		addEventListener('mousedown', this.globalListener, true);
		addEventListener('keyup', this.globalListener, true);
	}

	componentWillUnmount() {
		if (this.globalListener) {
			window.removeEventListener('click', this.globalListener, true);
			window.removeEventListener('keydown', this.globalListener, true);
		}
	}

	getCommonTableProps() {
		return {
			showCheckboxes: false,
			selectableRows: false,
			cellDefaultStyle: { paddingLeft: '3px', paddingRight: '3px' },
			identifier: (row) => row[this.props.dimension],
		};
	}

	setSearchString(str, extraState) {
		const newCandidates = this.findByString(str);
		this.updateState({
			searchStr: str,
			candidates: newCandidates,
			candidatesTotal: _.sumBy(newCandidates, 'metric'),
			chunkSz: CANDIDATE_CHUNK,
			...extraState,
		});
	}

	updateState(newState) {
		const { onSearchStringChanged } = this.props;
		if (onSearchStringChanged && ('searchStr' in newState)) {
			onSearchStringChanged(newState.searchStr);
		}
		this.setState(newState);
	}

	formatName(item, dim) {
		const { sysIdFld } = dim || {};
		return ((item[sysIdFld]) ? `${item.name} (${item[sysIdFld]})` : item.name);
	}

	async initAdvData(reportPromise, preSelectedAdvs) {
		const { createUsingDimObject: dim, metric, objectIdFld, excludeIdCb,
			dimension, displaySingleSelected, label, includeExternalId } = this.props;
		let hasResolved;
		let report;
		const setInitSearch = () => {
			const { searchStr } = this.state;
			const lastPreselected = _.last(preSelectedAdvs);
			const initSearchStr = searchStr || (displaySingleSelected && lastPreselected && this.formatName(lastPreselected, dim));
			this.setSearchString(initSearchStr || '');
		};

		const resolveIfNeeded = () => {
			if (!hasResolved) {
				this.initResolve();
				hasResolved = true;
				this.updateState({ advDataLoaded: true });
			}
		};
		const dimensionData = {
			// eslint-disable-next-line react/destructuring-assignment
			...this.state.dimensionData,
			byNr: _.mapValues(_.keyBy(preSelectedAdvs, objectIdFld), (adv) => ({
				[dimension]: adv[objectIdFld].toString(),
				name: this.formatName(adv, dim),
				externalId: adv.externalId,
				metric: 0,
			})),
		};
		this.updateState({ dimensionData });
		setInitSearch();
		try {
			report = await reportPromise;
		} catch (e) {
			this.initReject(e);
		}
		const advArr = _.map(report.data, (obj, advNr) => ({
			name: report.labels[label][advNr],
			metric: obj[metric],
			[dimension]: advNr,
			externalId: includeExternalId
				? (report.attributes[label][advNr] || {}).externalId
				: null,
		}));
		advArr.push(..._.values(_.pickBy(dimensionData.byNr, (__, nr) => !report.data[nr])));
		advArr.sort((a1, a2) => a2.metric - a1.metric);
		let start = new Date();
		const addTo = (dst, key, adv) => {
			const arrDst = dst[key];
			if (arrDst) {
				arrDst.push(adv);
			} else {
				dst[key] = [adv];
			}
		};
		const SMALL = SystemData.genericData.ADVERTISERS.ADVERTISER_TOO_SMALL_FOR_REPORT.toString();
		for (const adv of advArr) {
			if (excludeIdCb) {
				if (excludeIdCb(adv[dimension])) {
					continue;
				}
			} else if (adv[dimension] === SMALL) {
				continue;
			}
			dimensionData.byNr[adv[dimension]] = adv;
			adv.wordParts = {};
			splitStr(adv.name).forEach((word, wordIdx) => {
				for (let i = 1; i <= word.length; i++) {
					const wordPart = word.substr(0, i);
					if (!adv.wordParts[wordPart]) {
						adv.wordParts[wordPart] = true;
						addTo(dimensionData.byAnyWord, wordPart, adv);
						if (!wordIdx) {
							addTo(dimensionData.byFirstWord, wordPart, adv);
						}
					}
					adv.wordParts[wordPart] = true;
				}
				addTo(dimensionData.byAnyWordFull, word, adv);
			});
			if (new Date() - start > MAX_ADV_SETUP_CHUNK_MS) {
				resolveIfNeeded();
				await new Promise((r) => setTimeout(r));
				start = new Date();
			}
		}
		resolveIfNeeded();
		setInitSearch();
	}

	async createNew(searchStr) {
		const {
			createUsingDimObject: dim, checkForbidden, objectIdFld, dimension, selected, noObjectCreate,
		} = this.props;
		const { dimensionData } = this.state;
		const { sysIdFld } = dim;
		const cls = classes[dim.clsName];
		let adv = [(await cls.getObjects({ objNames: [searchStr] }))].flat();
		if (!adv.length) {
			const [, name, id] = searchStr.match(/^(.*) \(([^()]+)\)$/) || [];
			if (name && id) {
				adv = await cls.getObjects({ objNames: [name] });
				adv = adv.filter((o) => o[sysIdFld] === id);
			}
		}
		if (!adv || !adv.length) {
			if (noObjectCreate) {
				Base.renderGlobal((done) => (
					<Dialog
						open
						status="error"
						text={`No ${dim.name} named ${searchStr} exists in the system`}
						onClose={done}
					/>
				));
				return null;
			}
			const ok = await Base.renderGlobal((closeFn) => (
				<ConfirmDialog
					open
					text={(
						<span>
							{/* eslint-disable-next-line react/jsx-one-expression-per-line */}
							No {dim.name} named <b>{searchStr}</b>&nbsp;
							exists in the system, do you want to create it?
						</span>
					)}
					onAny={closeFn}
				/>
			));
			if (!ok) {
				return null;
			}
			adv = await cls.updateObjects({ objects: [{ name: searchStr }], returnObjects: true });
		}

		const validate = (item, nr) => {
			if (selected.includes(nr)) {
				return null;
			}
			const err = checkForbidden ? checkForbidden(nr) : null;
			if (err) {
				Base.renderGlobal((done) => (
					<Dialog
						open
						status="error"
						text={_.isString(err) ? err : 'Not allowed'}
						onClose={done}
					/>
				));
				return null;
			}

			dimensionData.byNr[nr] = {
				[dimension]: nr,
				name: this.formatName(item, dim),
				externalId: item.externalId,
				metric: 0,
			};
			return nr;
		};
		const advNrs = adv.map((d) => validate(d, d[objectIdFld].toString())).filter((d) => d);
		return advNrs;
	}

	async loadReport(params) {
		const { delayReportLoad, customizer, type } = this.props;
		const finalParams = customizer ? customizer.transformedReportParams(params) : params;
		if (delayReportLoad) {
			await this.hadInputEvent();
		}
		const res = await Reports.call(Constants[type].API_FN, finalParams);
		return res;
	}

	async load() {
		const {
			selected, noMapping, metric, groupBy, includeExternalId,
			overrideHistoryDaysBack, serverCall, label,
		} = this.props;
		const { DUMMY_NO_MAPPING } = SystemData.genericData.ADVERTISERS;
		const params = {
			groupBy: [groupBy],
			sums: [metric],
			maxAdvertisers: MAX_ADVERTISERS,
			start: DateUtils.fullDay(new Date(), -(overrideHistoryDaysBack || HISTORY_DAYS_BACK)),
			end: DateUtils.yesterday(),
			..._.mapValues(ByMappingProp, (d, fld) => (
				// eslint-disable-next-line react/destructuring-assignment
				d.dimension === groupBy && noMapping ? DUMMY_NO_MAPPING : this.props[fld]
			)),
			attributes: includeExternalId ? { [label]: { externalId: true } } : null,
		};
		if (!params.attributes) { // Don't circumvent report-cache just because an advertiers has been added/updated
			params.noSaveGenerationCacheClear = true;
		}
		const reportPromise = this.loadReport(params);
		const preSelectedAdvs = (selected.length)
			? await serverCall(selected)
			: [];
		this.initAdvData(reportPromise, preSelectedAdvs);
	}

	findByString(str) {
		const { dimension } = this.props;
		const sorted = (advArr) => _.sortBy(advArr, (adv) => -adv.metric);
		const { dimensionData } = this.state;
		const words = splitStr(str);
		if (!words.length) {
			return [];
		}
		const endsWithSpace = !!str[str.length - 1].match(SPLIT_REGEXP);

		/**
		 * When we're starting writing a word - during the first few characters (MAX_CHECK_FIRST_WORD_CHARS)
		 * only check 'byFirstWord' primarily. If we don't find anything and search string is VERY short
		 * (MAX_ONLY_CHECK_FIRST_WORD_CHARS) => give up and return nothing.
		 * */
		if (!endsWithSpace && words.length === 1 && words[0].length <= MAX_CHECK_FIRST_WORD_CHARS) {
			const res = dimensionData.byFirstWord[words[0]] || [];
			if (res.length || words[0].length <= MAX_ONLY_CHECK_FIRST_WORD_CHARS) {
				return sorted(res);
			}
		}
		let candidateSets = words.map((word, idx) => {
			if (idx === words.length - 1 && !endsWithSpace) {
				return dimensionData.byAnyWord[word] || []; // only last word without space after we'll match partially
			}
			return dimensionData.byAnyWordFull[word] || []; // other words must be 'full'
		});
		if (candidateSets.find((c) => !c.length)) {
			return [];
		}
		candidateSets = _.sortBy(candidateSets, 'length'); // start with smallest matching set for better performance
		let survivors;
		candidateSets.forEach((arr, idx) => { // For an advertiser to "survive" it must match all words
			const stillSurvives = {};
			arr.forEach((adv) => {
				if (!idx || survivors[adv[dimension]]) {
					stillSurvives[adv[dimension]] = adv;
				}
			});
			if (_.isEmpty(stillSurvives)) {
				survivors = {};
				return; // no match
			}
			survivors = stillSurvives;
		});
		return sorted(_.values(survivors));
	}

	numsToAdvs(arr) {
		const { dimensionData } = this.state;
		return _.filter(arr.map((nr) => dimensionData.byNr?.[nr]));
	}

	async notify(arr) {
		const { onChange, onSingleSelect, onlySingleSelect, displaySingleSelected } = this.props;
		const closeSearch = () => this.closeSearch();
		const options = { closeSearch };
		const advArr = this.numsToAdvs(arr);
		onChange(arr, advArr, options);
		const onSingleRes = await onSingleSelect(_.last(arr), _.last(advArr), options);
		if (onSingleRes !== false && onlySingleSelect) {
			if (displaySingleSelected) {
				this.setSearchString(_.last(advArr)?.name || '');
			}
			closeSearch();
		}
	}

	closeSearch() {
		const { displaySingleSelected } = this.props;
		this.updateState({
			showSearchResult: false,
			...(!displaySingleSelected && { searchStr: '' }),
		});
	}

	// eslint-disable-next-line react/no-unused-class-component-methods
	getSearchString() {
		const { searchStr } = this.state;
		return searchStr;
	}

	isUseNameEnabled() {
		const { selected, createUsingDimObject } = this.props;
		const { searchStr } = this.state;
		return createUsingDimObject && searchStr && !_.map(this.numsToAdvs(selected), 'name').includes(searchStr);
	}

	/** Returns true => name used, false => nothing to do, null => user aborted */
	async applyUseName() {
		const { selected } = this.props;
		const { searchStr } = this.state;
		if (!this.isUseNameEnabled()) {
			return false;
		}
		const advNr = await this.createNew(searchStr);
		if (advNr?.length) { // null if not valid else [...]
			this.closeSearch();
			await this.notify((selected || []).concat(...advNr));
		}
		return advNr?.length ? true : null;
	}

	renderIcon(advNr) {
		const { selected, checkForbidden, noDelete } = this.props;
		const forbidden = checkForbidden && checkForbidden(advNr);
		if (forbidden) {
			return (
				<Tooltip title={_.isString(forbidden) ? forbidden : 'Not allowed'}>
					<InfoIcon color="info" />
				</Tooltip>
			);
		}
		if (selected.includes(advNr)) {
			if (noDelete) {
				return (
					<IconButton size="large" style={{ color: 'gray' }}>
						<DoneIcon />
					</IconButton>
				);
			}
			return (
				<IconButton onClick={() => this.notify(_.without(selected, advNr))} size="large">
					<MinusIcon color="error" />
				</IconButton>
			);
		}
		return (
			<IconButton
				onClick={() => {
					this.notify(selected.concat(advNr));
				}}
				size="large"
			>
				<Box component="span" color="success.light">
					<AddIcon />
				</Box>
			</IconButton>
		);
	}

	renderEndAdornment() {
		const { displaySingleSelected } = this.props;
		const { searchStr } = this.state;
		if (this.isUseNameEnabled()) {
			return (
				<JobButton
					label="Use name"
					size="s"
					fn={() => this.applyUseName()}
				/>
			);
		}
		if (displaySingleSelected && searchStr) {
			return (
				<IconButton onClick={() => this.setSearchString('')}>
					<CloseIcon fontSize="small" />
				</IconButton>
			);
		}
		return null;
	}

	render() {
		const {
			selected, onlySingleSelect, hideEmptySelected, extraTableDefs, textFieldLabel,
			includeExternalId, fullWidth, margin, dimension, showMoreLabel, textFieldProps,
		} = this.props;
		const {
			candidates, candidatesTotal, showSearchResult, chunkSz, advDataLoaded,
		} = this.state;
		const selectedArr = MiscUtils.alphaSorted(this.numsToAdvs(selected), 'name');
		return (
			<OperationWrapper fn={() => this.load()}>
				<div
					onBlur={(ev) => {
						ev.preventDefault();
						this.searchResClicked = false;
						setTimeout(() => {
							if (!this.searchResClicked) {
								this.updateState({ showSearchResult: false });
							}
						});
					}}
					onFocus={(ev) => {
						if (ev.target instanceof HTMLInputElement) {
							this.hadInputEvent.trigger();
							this.updateState({ showSearchResult: true });
						}
					}}
					ref={(elm) => {
						if (elm) {
							const input = elm.getElementsByTagName('INPUT')[0];
							if (input) {
								input.setAttribute('autocomplete', 'off');
							}
						}
					}}
				>
					<TextField
						label={textFieldLabel}
						name="searchAdvertiser"
						value={this.getSearchString()}
						onChange={(ev) => {
							this.hadInputEvent.trigger();
							this.setSearchString(ev.target.value, { showSearchResult: true });
						}}
						fullWidth={fullWidth}
						margin={margin}
						endAdornment={this.renderEndAdornment()}
						{...textFieldProps}
					/>
					{showSearchResult && !!(this.getSearchString() || '').trim().length
					&& (
						<OperationWrapper
							disableMode
							fn={async () => {
								await this.initAdvDataPromise;
								this.setSearchString(this.getSearchString());
							}}
						>
							<div style={{ position: 'relative' }}>
								<div
									role="none"
									style={{ position: 'absolute', zIndex: 2 }}
									ref={(elm) => { this.searchContainerElm = elm; }}
									onMouseDown={() => {
										setTimeout(() => {
											this.searchResClicked = true;
										});
									}}
								>
									<Card style={{ marginTop: -10, marginBottom: 0 }}>
										<CardContent>
											<DataTable
												{...this.getCommonTableProps()}
												noHeader
												noDataMessage={advDataLoaded ? undefined : loadingMarker()}
												definitions={[
													{
														key: dimension,
														title: 'Add',
														style: { width: '40px' },
														padding: 'none',
														format: (advNr) => this.renderIcon(advNr),
													},
													{
														key: 'name',
														title: 'Name',
													},
													{
														key: 'metric',
														title: 'Daily revenue',
														format: (rev) => `${((rev / (candidatesTotal || 1)) * 100).toFixed(2)}%`,
													},
												].concat(includeExternalId ? [
													{
														key: 'advNr',
														title: 'External Id',
														format: (advNr, adv) => ((adv.externalId)
															? (<span style={{ fontWeight: 'bold' }}>{adv.externalId}</span>)
															: (<Box component="em" color="grey.600">(No external id)</Box>)),
													},
												] : [], extraTableDefs)}
												data={_.take(candidates, chunkSz)}
											/>
											{candidates.length > chunkSz
										&& (
											<Button
												onClick={() => {
													this.updateState({ chunkSz: chunkSz + CANDIDATE_CHUNK });
												}}
												fullWidth
											>
												{`Show ${candidates.length - chunkSz} more ${showMoreLabel}`}
											</Button>
										)}
										</CardContent>
									</Card>
								</div>
							</div>
						</OperationWrapper>
					)}
				</div>
				{!onlySingleSelect && !(!selectedArr.length && hideEmptySelected) && (
					<DataTable
						{...this.getCommonTableProps()}
						definitions={[
							{
								key: dimension,
								title: '',
								style: { width: 150 },
								headerElm: selectedArr.length && (
									<ActionButton
										label="Clear all"
										color="primary"
										onClick={() => this.notify([])}
										icon={<DeleteIcon />}
									/>
								),
								format: (advNr) => (
									<DeleteButton
										onClick={() => this.notify(_.without(selected, advNr))}
									/>
								),
							},
							{
								key: 'name',
								title: '',
								style: { wordBreak: 'break-all' },
							},
						]}
						data={selectedArr}
					/>
				)}
			</OperationWrapper>
		);
	}
}

DynamicSearchFilter.propTypes = {
	selected: PropTypes.arrayOf(PropTypes.string),
	onChange: PropTypes.func,
	groupBy: PropTypes.string,
	onSingleSelect: PropTypes.func,
	type: PropTypes.string.isRequired,
	metric: PropTypes.string,
	onlySingleSelect: PropTypes.bool,
	hideEmptySelected: PropTypes.bool,
	noMapping: PropTypes.bool,
	textFieldLabel: PropTypes.string,
	includeExternalId: PropTypes.bool,
	dimension: PropTypes.string.isRequired,
	serverCall: PropTypes.func.isRequired,
	label: PropTypes.string,
	extraTableDefs: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
	fullWidth: PropTypes.bool,
	margin: PropTypes.oneOf(['none', 'dense', 'normal']),
	showMoreLabel: PropTypes.string,
	customizer: PropTypes.object,
	objectIdFld: PropTypes.string,
	excludeIdCb: PropTypes.func,
	overrideHistoryDaysBack: PropTypes.number,
	checkForbidden: PropTypes.func,
	onSearchStringChanged: PropTypes.func,
	createUsingDimObject: PropTypes.object,
	noObjectCreate: PropTypes.bool,
	displaySingleSelected: PropTypes.bool,
	noDelete: PropTypes.bool,
	textFieldProps: PropTypes.object,
	delayReportLoad: PropTypes.bool,
	..._.mapValues(ByMappingProp, () => PropTypes.string),
};

DynamicSearchFilter.defaultProps = {
	selected: [],
	groupBy: 'advertiserNr',
	metric: 'revenue',
	onlySingleSelect: false,
	onChange: () => {},
	onSingleSelect: () => {},
	serverCall: () => {},
	hideEmptySelected: false,
	noMapping: false,
	dimension: 'advNr',
	label: 'advertiserNr',
	extraTableDefs: [],
	textFieldLabel: 'Add Advertiser(s)',
	includeExternalId: false,
	fullWidth: false,
	margin: undefined,
	showMoreLabel: 'advertisers',
	customizer: undefined,
	objectIdFld: 'seq',
	excludeIdCb: undefined,
	overrideHistoryDaysBack: 0, // 0 => Use default
	checkForbidden: undefined,
	onSearchStringChanged: undefined,
	createUsingDimObject: undefined,
	noObjectCreate: false,
	displaySingleSelected: false,
	noDelete: false,
	textFieldProps: undefined,
	delayReportLoad: false,
	..._.mapValues(ByMappingProp, () => null),
};

export default DynamicSearchFilter;
export { HISTORY_DAYS_BACK };
